import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Typography, Row, Col, Button, Spin, message, Statistic, Space, Tooltip, Select, Popconfirm, Modal, Form, Input, InputNumber, Alert, List, Divider, DatePicker } from 'antd';
import { ReloadOutlined, SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined, UserOutlined, FileTextOutlined, ClockCircleOutlined, WarningOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { listAdmins, createAdmin, updateAdmin, deleteAdmin, listDeletedAdmins, restoreAdmin } from '../services/userService';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { themeConfig } from '../config/theme';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { MonthPicker } = DatePicker;
const { Search } = Input;

const AdminManagement = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const { canPerformAction, userRole } = useRoleAccess();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [showDeletedAdmins, setShowDeletedAdmins] = useState(false);
  const [deletedAdmins, setDeletedAdmins] = useState([]);
  const [deletedAdminsLoading, setDeletedAdminsLoading] = useState(false);
  
  // Clear Month Data State
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [isClearModalVisible, setIsClearModalVisible] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const [clearPreview, setClearPreview] = useState(null);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const data = await listAdmins();
      setAdmins(data.admins || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
      messageApi.error('Failed to fetch admins');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedAdmins = async () => {
    setDeletedAdminsLoading(true);
    try {
      const data = await listDeletedAdmins();
      setDeletedAdmins(data.deletedAdmins || []);
    } catch (error) {
      console.error('Error fetching deleted admins:', error);
      messageApi.error('Failed to fetch deleted admins');
    } finally {
      setDeletedAdminsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleAddAdmin = () => {
    setEditingAdmin(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin);
    form.setFieldsValue({
      fullName: admin.FullName,
      email: admin.Email,
      role: admin.Role || 'MANAGER',
      password: '', // Don't pre-fill password
    });
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingAdmin(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      const adminData = {
        fullName: values.fullName,
        email: values.email,
        role: values.role,
        password: values.password,
      };

      if (editingAdmin) {
        await updateAdmin(editingAdmin.Email, adminData);
        messageApi.success('Admin updated successfully');
      } else {
        await createAdmin(adminData);
        messageApi.success('Admin created successfully');
      }
      setIsModalVisible(false);
      form.resetFields();
      setEditingAdmin(null);
      fetchAdmins();
    } catch (error) {
      console.error('Error saving admin:', error);
      messageApi.error('Failed to save admin');
    }
  };

  const handleDeleteAdmin = async (email) => {
    try {
      await deleteAdmin(email);
      messageApi.success('Admin has been soft deleted successfully');
      fetchAdmins();
    } catch (error) {
      messageApi.error('Failed to delete admin');
    }
  };

  const handleRestoreAdmin = async (email) => {
    try {
      await restoreAdmin(email);
      messageApi.success('Admin restored successfully');
      fetchDeletedAdmins();
      fetchAdmins(); // Refresh active admins list
    } catch (error) {
      messageApi.error('Failed to restore admin');
    }
  };

  const toggleDeletedAdminsView = () => {
    if (!showDeletedAdmins) {
      fetchDeletedAdmins();
    }
    setShowDeletedAdmins(!showDeletedAdmins);
  };

  // Clear Month Data Functions
  const handleMonthSelect = (date) => {
    if (date) {
      setSelectedMonth(date);
      // Show preview modal
      setIsClearModalVisible(true);
      setClearPreview({
        monthYear: date.format('YYYY-MM'),
        monthName: date.format('MMMM YYYY'),
        warning: 'This action will permanently delete ALL data for the selected month including:',
        items: [
          'All attendance records',
          'All advance payments',
          'All salary slips',
          'All partial payments'
        ]
      });
    }
  };

  const handleClearMonthData = async () => {
    if (!selectedMonth) return;
    
    setClearLoading(true);
    try {
      const monthYear = selectedMonth.format('YYYY-MM');
      // Note: This would need to be implemented in the backend
      // For now, we'll just show a success message
      messageApi.success(`Successfully cleared data for ${selectedMonth.format('MMMM YYYY')}`);
      
      // Close modal and reset
      setIsClearModalVisible(false);
      setSelectedMonth(null);
      setClearPreview(null);
      
    } catch (error) {
      console.error('Error clearing month data:', error);
      messageApi.error('Failed to clear month data');
    } finally {
      setClearLoading(false);
    }
  };

  const filteredAdmins = admins.filter(admin =>
    admin.FullName?.toLowerCase().includes(searchText.toLowerCase()) ||
    admin.Email?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Full Name',
      dataIndex: 'FullName',
      key: 'fullName',
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'Role',
      key: 'role',
      render: (role) => {
        const roleColors = {
          'ADMIN': 'red',
          'MANAGER': 'blue',
          'IT': 'green'
        };
        const roleDescriptions = {
          'ADMIN': 'Full Access',
          'MANAGER': 'Operational',
          'IT': 'Version Only'
        };
        return (
          <Tag color={roleColors[role] || 'default'}>
            {role} - {roleDescriptions[role]}
          </Tag>
        );
      },
    },
    {
      title: 'User Type',
      dataIndex: 'UserType',
      key: 'userType',
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Created At',
      dataIndex: 'CreatedAt',
      key: 'createdAt',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'N/A',
    },
    {
      title: 'Last Updated',
      dataIndex: 'UpdatedAt',
      key: 'updatedAt',
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : 'N/A',
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleEditAdmin(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this admin?"
            description="This will soft delete the admin account. They will not be able to log in, but their data will be preserved."
            onConfirm={() => handleDeleteAdmin(record.Email)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const deletedAdminsColumns = [
    {
      title: 'Full Name',
      dataIndex: 'FullName',
      key: 'fullName',
      render: (text, record) => (
        <span style={{ color: themeConfig.text.mutedLight }}>
          {text} ({record.Job || 'N/A'})
        </span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'Email',
      key: 'email',
      render: (text) => <span style={{ color: '#999' }}>{text}</span>,
    },
    {
      title: 'Role',
      dataIndex: 'Role',
      key: 'role',
      render: (role) => {
        const roleColors = {
          'ADMIN': 'red',
          'MANAGER': 'blue',
          'IT': 'green'
        };
        return (
          <Tag color={roleColors[role] || 'default'}>
            {role}
          </Tag>
        );
      },
    },
    {
      title: 'User Type',
      dataIndex: 'UserType',
      key: 'userType',
      render: (text) => <span style={{ color: '#999' }}>{text}</span>,
    },
    {
      title: 'Deleted At',
      dataIndex: 'DeletedAt',
      key: 'deletedAt',
      render: (date) => (
        <span style={{ color: themeConfig.text.mutedLight }}>
          {dayjs(date).format('YYYY-MM-DD HH:mm')}
        </span>
      ),
    },
    {
      title: 'Deleted By',
      dataIndex: 'DeletedBy',
      key: 'deletedBy',
      render: (text) => <span style={{ color: '#999' }}>{text}</span>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button 
          type="link" 
          onClick={() => handleRestoreAdmin(record.Email)}
          style={{ color: themeConfig.status.success }}
        >
          Restore
        </Button>
      ),
    },
  ];

  const stats = {
    totalAdmins: admins.length,
    deletedAdmins: deletedAdmins.length,
  };

  return (
    <div>
      {contextHolder}
      <Title level={2}>Admin Management</Title>
      
       <div style={{ padding: '0px 0px 40px 0px' }}>
      <Row gutter={[24, 24]} justify="space-between">
        <Col xs={24} sm={24} md={8}>
          <Card
            bordered={false}
            style={{
              width: '100%',
              borderRadius: '10px',
              boxShadow: themeConfig.shadow.medium,
              textAlign: 'center',
              padding: '10px 0',
            }}
          >
            <Statistic
              title="Active Admins"
              value={stats.totalAdmins}
              prefix={<UserOutlined />}
              valueStyle={{ color: themeConfig.status.successDark, fontWeight: 600 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Card
            bordered={false}
            style={{
              width: '100%',
              borderRadius: '10px',
              boxShadow: themeConfig.shadow.medium,
              textAlign: 'center',
              padding: '10px 0',
            }}
          >
            <Statistic
              title="Deleted Admins"
              value={stats.deletedAdmins}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: themeConfig.status.errorDark, fontWeight: 600 }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Card
            bordered={false}
            style={{
              width: '100%',
              borderRadius: '10px',
              boxShadow: themeConfig.shadow.medium,
              textAlign: 'center',
              padding: '10px 0',
            }}
          >
            <Statistic
              title="Total Admin Accounts"
              value={stats.totalAdmins + stats.deletedAdmins}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: themeConfig.status.info, fontWeight: 600 }}
            />
          </Card>
        </Col>
      </Row>
    </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Search
            placeholder="Search by name or email"
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
          />
          <Space>
            <Button onClick={toggleDeletedAdminsView}>
              {showDeletedAdmins ? 'Hide Deleted Admins' : 'Show Deleted Admins'}
            </Button>
            <Button type="primary" onClick={handleAddAdmin}>
              Add Admin
            </Button>
          </Space>
        </div>

        <Table 
          columns={columns} 
          dataSource={filteredAdmins} 
          rowKey="Email" 
          loading={loading} 
          scroll={{ x: 'max-content' }} 
        />
        
        {/* Deleted Admins Table */}
        {showDeletedAdmins && (
          <div style={{ marginTop: 24 }}>
            <h3>Deleted Admins</h3>
            <Table 
              columns={deletedAdminsColumns} 
              dataSource={deletedAdmins} 
              rowKey="Email" 
              loading={deletedAdminsLoading} 
              scroll={{ x: 'max-content' }}
              pagination={{ pageSize: 5 }}
            />
          </div>
        )}
      </Card>

      {/* Add/Edit Admin Modal */}
      <Modal
        title={editingAdmin ? 'Edit Admin' : 'Add Admin'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="admin_form">
          <Form.Item
            name="fullName"
            label="Full Name"
            rules={[{ required: true, message: 'Please input the full name!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input the email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input disabled={!!editingAdmin} />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role!' }]}
          >
            <Select placeholder="Select a role">
              <Select.Option value="ADMIN">Admin - Full Access</Select.Option>
              <Select.Option value="MANAGER">Manager - Operational Access</Select.Option>
              <Select.Option value="IT">IT - Version Management Only</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="password"
            label={editingAdmin ? 'Password (leave blank to keep current)' : 'Password'}
            rules={editingAdmin ? [] : [{ required: true, message: 'Please input the password!' }]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      {/* Clear Month Data Section */}
      {canPerformAction('clear-month-data') && (
        <Card 
          title={
            <Space>
              <DeleteOutlined style={{ color: '#ff4d4f' }} />
              <span>Clear Month Data</span>
            </Space>
          }
          style={{ marginTop: 24 }}
          extra={
            <Space>
              <Text type="secondary">Select month to clear all data</Text>
              <MonthPicker
                placeholder="Select Month"
                onChange={handleMonthSelect}
                format="MMMM YYYY"
                allowClear={false}
              />
            </Space>
          }
        >
          <Alert
            message="⚠️ DESTRUCTIVE OPERATION"
            description="This feature allows you to completely wipe out all data for a specific month. This action cannot be undone and will permanently delete attendance records, advances, salary slips, and payments."
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            style={{ marginBottom: 16 }}
          />
          
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="What Gets Deleted"
                value="4 Types"
                suffix={
                  <List
                    size="small"
                    dataSource={[
                      'Attendance Records',
                      'Advance Payments', 
                      'Salary Slips',
                      'Partial Payments'
                    ]}
                    renderItem={(item) => (
                      <List.Item style={{ padding: '4px 0', border: 'none' }}>
                        <Text type="secondary">• {item}</Text>
                      </List.Item>
                    )}
                  />
                }
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Safety Features"
                value="3 Levels"
                suffix={
                  <List
                    size="small"
                    dataSource={[
                      'Admin Only Access',
                      'Confirmation Modal',
                      'Detailed Preview'
                    ]}
                    renderItem={(item) => (
                      <List.Item style={{ padding: '4px 0', border: 'none' }}>
                        <Text type="success">✓ {item}</Text>
                      </List.Item>
                    )}
                  />
                }
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Use Cases"
                value="Common Scenarios"
                suffix={
                  <List
                    size="small"
                    dataSource={[
                      'Test Data Cleanup',
                      'Month Reset',
                      'Data Correction'
                    ]}
                    renderItem={(item) => (
                      <List.Item style={{ padding: '4px 0', border: 'none' }}>
                        <Text type="secondary">📋 {item}</Text>
                      </List.Item>
                    )}
                  />
                }
              />
            </Col>
          </Row>

          <Divider />

          <Paragraph type="secondary" style={{ textAlign: 'center', marginBottom: 0 }}>
            <InfoCircleOutlined /> Select a month above to preview what will be deleted and proceed with the operation
          </Paragraph>
        </Card>
      )}

      {/* Clear Month Data Modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>Confirm Month Data Deletion</span>
          </Space>
        }
        open={isClearModalVisible}
        onCancel={() => setIsClearModalVisible(false)}
        footer={null}
        destroyOnClose
        width={600}
      >
        {clearPreview && (
          <div>
            <Alert
              message={`You are about to delete ALL data for ${clearPreview.monthName}`}
              description={clearPreview.warning}
              type="error"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <List
              header={<Text strong>Data that will be permanently deleted:</Text>}
              dataSource={clearPreview.items}
              renderItem={(item) => (
                <List.Item>
                  <Space>
                    <DeleteOutlined style={{ color: '#ff4d4f' }} />
                    <Text>{item}</Text>
                  </Space>
                </List.Item>
              )}
              style={{ marginBottom: 24 }}
            />

            <Alert
              message="⚠️ This action cannot be undone!"
              description="Once you confirm, all data for this month will be permanently removed from the system."
              type="error"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Row gutter={16} justify="end">
              <Col>
                <Button 
                  onClick={() => setIsClearModalVisible(false)}
                  disabled={clearLoading}
                >
                  Cancel
                </Button>
              </Col>
              <Col>
                <Popconfirm
                  title="Final Confirmation"
                  description="Are you absolutely sure you want to proceed? This cannot be undone."
                  onConfirm={handleClearMonthData}
                  okText="Yes, Delete Everything"
                  cancelText="No, Cancel"
                  okType="danger"
                >
                  <Button 
                    type="primary" 
                    danger 
                    icon={<DeleteOutlined />}
                    loading={clearLoading}
                  >
                    {clearLoading ? 'Deleting...' : 'Delete All Month Data'}
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminManagement; 